import { TiledGameObject } from "src/games/shared/phaserHelpers/HandyTypes";
import bindAllOfThis from "src/utils/BindThisHelper";
import { PhaserGamePad } from "src/games/shared/Gamepad";

export class MainScene extends Phaser.Scene {
    gameWidth: number;
    gameHeight: number;
    map: Phaser.Tilemaps.Tilemap;
    player: Phaser.Physics.Arcade.Sprite;
    groundLayer: Phaser.Tilemaps.DynamicTilemapLayer;
    npcLayer: Phaser.Tilemaps.ObjectLayer;
    boothLayer: Phaser.Tilemaps.ObjectLayer;
    doubleJump: boolean = true;
    gamePad: PhaserGamePad;
    speed: number = 180;
    npcs: Phaser.Physics.Arcade.Sprite[];
    currentNPC: Phaser.Physics.Arcade.Sprite;
    playerAlwaysLeads: boolean = false;

    constructor() {
        super({
            key: "MainScene"
        });
    }

    preload(): void {
        this.gameWidth = this.textures.game.config.width as number;
        this.gameHeight = this.textures.game.config.height as number;
        this.load.image('drawtiles', require("/src/assets/qci/tilemaps/tiles/drawtiles.png"));
        this.load.tilemapTiledJSON('map', require("/src/assets/qci/maps/npc_following_demo_map.json"));
        this.load.image("pixel", require("/src/assets/qci/sprites/pixel.png"));
        this.load.image("astronaut", require("/src/assets/qci/sprites/astronaut.png"));
        this.load.image("exclamation-point", require("/src/assets/qci/sprites/exclamation-point-icon.png"));
        this.load.spritesheet('dude', require('src/assets/phaser-assets/sprites/dude.png'), { frameWidth: 32, frameHeight: 48 });

        PhaserGamePad.preload(this);
    }

    create(): void {
        bindAllOfThis(this, MainScene.prototype);
        this.initializeMap();
        this.generateScene();
    }

    createGamePad(): void {
        this.gamePad = new PhaserGamePad(this);

        this.gamePad.on("keydown_up", this.moveUp);
        this.gamePad.on("keydown_down", this.moveDownStart);
        this.gamePad.on("keydown_left", this.moveLeft);
        this.gamePad.on("keydown_right", this.moveRight);
        this.gamePad.on("keydown_a", this.aDownStart);
        this.gamePad.on("keydown_b", this.bDownStart);

        this.gamePad.on("keyup_up", this.moveUpStop);
        this.gamePad.on("keyup_down", this.moveDownStop);
        this.gamePad.on("keyup_left", this.stopLeftRight);
        this.gamePad.on("keyup_right", this.stopLeftRight);
        this.gamePad.on("keyup_a", this.stopAB);
        this.gamePad.on("keyup_b", this.stopAB);
    }

    // Initializes the tilemap and sets tile collision properties
    initializeMap(): void {
        this.map = this.make.tilemap({ key: 'map' });
        var groundTiles = this.map.addTilesetImage('drawtiles');
        this.map.addTilesetImage('dude').setTileSize(32, 48);
        this.map.createDynamicLayer('Background Layer', groundTiles, 0, 0);
        this.groundLayer = this.map.createDynamicLayer('Ground Layer', groundTiles, 0, 0);
        this.npcLayer = this.map.getObjectLayer('NPC Layer');

        // Tiles with IDs 10-24 will cause collision
        this.groundLayer.setCollisionBetween(10, 24);

        // Allow collision from the bottom only on tile 20
        this.groundLayer.layer.data.forEach((row: Phaser.Tilemaps.Tile[]) => {
            row.forEach(tile => {
                if (tile.index === 20) {
                    tile.setCollision(false, false, true, false);
                }
            });
        });
    }

    generateScene() {
        this.generatePlayer();
        this.generateNPCs();
        this.createGamePad();

        // Setup the camera to follow the player
        this.cameras.main.setBounds(0, 0, this.map.widthInPixels, this.map.heightInPixels);
        this.cameras.main.startFollow(this.player);
    }

    generatePlayer(): void {
        this.player = this.physics.add.sprite(50, 100, "dude");
        this.player.setBounce(0.2);

        //  Player animations, turning, walking left and walking right.
        this.anims.create({
            key: 'left',
            frames: this.anims.generateFrameNumbers('dude', { start: 0, end: 3 }),
            frameRate: 10,
            repeat: -1
        });

        this.anims.create({
            key: 'turn',
            frames: [{ key: 'dude', frame: 4 }],
            frameRate: 20
        });

        this.anims.create({
            key: 'right',
            frames: this.anims.generateFrameNumbers('dude', { start: 5, end: 8 }),
            frameRate: 10,
            repeat: -1
        });

        this.player.setDepth(100);
        this.physics.add.collider(this.player, this.groundLayer);
    }

    /**
     * Generates the astronaut and candidates with their careers
     * */
    generateNPCs(): void {
        var i = 0;
        this.npcs = [];
        this.npcLayer.objects.forEach((ob: TiledGameObject) => {
            var npc = this.physics.add.sprite(ob.x + 16, ob.y + 10, "dude");
            npc.setTint(0x4286f4);
            npc.setFrame(4);
            npc.setDataEnabled();
            npc.setData('enabled', true);
            npc.setInteractive();
            npc.setDepth(0);
            npc.on('pointerdown', () => {
                // Start following
                this.currentNPC = npc;
                this.physics.moveToObject(this.currentNPC, this.player, this.speed);
            });
            this.physics.add.collider(npc, this.groundLayer);
            this.physics.add.overlap(npc, this.player);
            this.npcs.push(npc);
        });
    }

    /**
     * Handles user input
     * */
    update(): void {
        //  Player Movement
        let cursors = this.input.keyboard.createCursorKeys();

        if (this.currentNPC != null) {
            this.updateNPC();
        }

        if (cursors.left.isDown || this.gamePad.keys.left.isDown) {
            this.moveLeft();
        }
        else if (cursors.right.isDown || this.gamePad.keys.right.isDown) {
            this.moveRight();
        }
        else {
            this.stopLeftRight();
        }

        if (cursors.up.isDown || this.gamePad.keys.up.isDown) {
            this.moveUp();
        }

        // Check if we are back on the ground to reset the second jump
        if (!this.doubleJump && this.player.body.onFloor()) {
            this.doubleJump = true;
        }
    }

    updateNPC(): void {
        if (this.currentNPC.body.velocity.x > 0) {
            this.currentNPC.anims.play('right', true);
            this.physics.moveTo(this.currentNPC, this.player.getBottomLeft().x - 2 * this.player.width, this.player.y, this.speed);
        }
        else if (this.currentNPC.body.velocity.x < 0) {
            this.currentNPC.anims.play('left', true);
            this.physics.moveTo(this.currentNPC, this.player.getBottomRight().x + 2 * this.player.width, this.player.y, this.speed);
        }
    }

    moveUp() {
        if (this.player.body.onFloor()) {
            this.attemptJump();
        }
    }

    moveDownStart() {
        this.player.setVelocityY(this.speed);

        if (this.currentNPC != null) {
            this.currentNPC.setVelocityY(this.speed);
        }
    }

    moveLeft() {
        this.player.setVelocityX(-this.speed);
        this.player.anims.play('left', true);

        if (this.currentNPC != null) {
            this.moveNPCLeft();
        }
    }

    moveNPCLeft(): void {
        if (this.playerAlwaysLeads) {
            // Only move the NPC if they're not too far away
            if (!this.isNPCTooFarAway()) {
                if (this.isNPCLeftOfPlayer()) {
                    // Moving left and npc is on the left, so move them to the right of the player
                    var dest;
                    var x = this.player.getCenter().x + this.player.width;
                    // If the player is under the current npc, move to them
                    if (this.player.y < this.currentNPC.y) {
                        dest = new Phaser.Geom.Rectangle(x, this.player.getCenter().y, this.currentNPC.width, this.currentNPC.height);
                    }
                    else {
                        // The player is above the npc, so don't move up to it
                        dest = new Phaser.Geom.Rectangle(x, this.currentNPC.getCenter().y, this.currentNPC.width, this.currentNPC.height);
                    }
                    this.physics.moveToObject(this.currentNPC, dest, this.speed);
                }
            }
        }
        else {
            this.currentNPC.setVelocityX(-this.speed);
        }
    }

    moveRight() {
        this.player.setVelocityX(this.speed);
        this.player.anims.play('right', true);

        if (this.currentNPC != null) {
            this.moveNPCRight();
        }
    }

    moveNPCRight(): void {
        if (this.playerAlwaysLeads) {
            // Only move the NPC if they're not too far away
            if (!this.isNPCTooFarAway()) {
                if (this.isNPCRightOfPlayer()) {
                    this.currentNPC.setVelocityX(this.speed);
                    // Moving right and npc is on the right, so move them over to the left of the npc
                    var dest;
                    var x = this.player.getCenter().x - this.player.width;
                    // If the player is under the current npc, move to them
                    if (this.player.y < this.currentNPC.y) {
                        dest = new Phaser.Geom.Rectangle(x, this.player.getCenter().y, this.currentNPC.width, this.currentNPC.height);
                    }
                    else {
                        // The player is above the npc, so don't move up to it
                        dest = new Phaser.Geom.Rectangle(x, this.currentNPC.getCenter().y, this.currentNPC.width, this.currentNPC.height);
                    }
                    this.physics.moveToObject(this.currentNPC, dest, this.speed);
                }
                else {
                    this.currentNPC.setVelocityX(this.speed);
                }
            }
        }
        else {
            this.currentNPC.setVelocityX(this.speed);
        }
    }


    isNPCRightOfPlayer(): boolean {
        return this.currentNPC.x > (this.player.x - this.player.width);
    }

    isNPCLeftOfPlayer(): boolean {
        return this.currentNPC.x <= (this.player.x + this.player.width);
    }

    isNPCTooFarAway(): boolean {
        return false;
        // return Math.abs(this.currentNPC.x - this.player.x) > 100;
    }

    moveUpStop() {
        // NOOP
    }

    moveDownStop() {
        this.player.setVelocityY(this.speed);
        this.player.anims.play('turn');
    }

    stopLeftRight() {
        this.player.setVelocityX(0);
        this.player.anims.play('turn');

        if (this.currentNPC != null) {
            this.currentNPC.setVelocityX(0);
            this.currentNPC.anims.play('turn');
        }
    }

    aDownStart() {

    }

    bDownStart() {
        // NOOP
    }

    stopAB() {
        // NOOP
    }

    attemptJump() {
        if (this.player.body.onFloor()) {
            this.doubleJump = true;
            this.player.body.setVelocityY(-400);
        } else if (this.doubleJump) {
            this.doubleJump = false;
            this.player.body.setVelocityY(-400);
        }

        // Delay the npc jump
        if (this.currentNPC != null && !this.isNPCTooFarAway()) {
            setTimeout(() => {
                if (this.currentNPC.body.onFloor()) {
                    this.doubleJump = true;
                    this.currentNPC.body.setVelocityY(-400);
                } else if (this.doubleJump) {
                    this.doubleJump = false;
                    this.currentNPC.body.setVelocityY(-400);
                }
            }, 200);
        }
    }
}



// WEBPACK FOOTER //
// ./src/games/demos/npcFollowingDemo/scenes/MainScene.ts